import React, { useState, useEffect, useMemo } from 'react';
import { ref, get, set, remove } from 'firebase/database';
import { db } from '../firebase';
import { useAuthContext } from '../contexts/AuthContext';
import { useAllStaffMembers } from '../hooks/useStaffMembers';
import { useInvitations } from '../hooks/useInvitations';
import { useConfirm } from '../contexts/ConfirmContext';
import { getLimits, formatMax } from '../utils/planLimits';

const StaffPage = ({ events }) => {
  const { user, userProfile, isSuperAdmin } = useAuthContext();
  const { staffByEvent, loading } = useAllStaffMembers(events);
  const { createInviteLink } = useInvitations();
  const confirm = useConfirm();
  const limits = getLimits(userProfile?.license?.plan);

  const [selectedEventId, setSelectedEventId] = useState('all');
  const [editingMemo, setEditingMemo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(null);

  const uniqueStaffCount = useMemo(() => {
    const uids = new Set();
    Object.values(staffByEvent).forEach((members) =>
      Object.keys(members).forEach((uid) => uids.add(uid))
    );
    return uids.size;
  }, [staffByEvent]);

  const isStaffLimitReached = !isSuperAdmin && uniqueStaffCount >= limits.maxStaff;

  // display_name バックフィル
  useEffect(() => {
    const backfill = async () => {
      const tasks = [];
      Object.entries(staffByEvent).forEach(([eventId, members]) => {
        Object.entries(members).forEach(([uid, member]) => {
          if (!member.display_name) {
            tasks.push(
              get(ref(db, `users/${uid}/display_name`)).then((snap) => {
                if (snap.exists())
                  return set(ref(db, `events/${eventId}/staff_members/${uid}/display_name`), snap.val());
              })
            );
          }
        });
      });
      if (tasks.length > 0) await Promise.all(tasks);
    };
    backfill();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(staffByEvent)]);

  const handleCopyInviteLink = async (eventId) => {
    if (isStaffLimitReached) {
      alert(`スタッフの上限（${limits.maxStaff}人）に達しています。\nProプランにアップグレードすると無制限に招待できます。`);
      return;
    }
    setInviteLoading(eventId);
    const result = await createInviteLink({ eventId, ownerId: user.uid });
    if (result.success) {
      alert('招待リンクをクリップボードにコピーしました！\n有効期限：7日間');
    } else {
      alert(`エラー: ${result.error}`);
    }
    setInviteLoading(null);
  };

  const handleRemoveStaff = async (eventId, staffUid, displayName) => {
    const name = displayName || staffUid;
    const ok = await confirm({
      message: `「${name}」をこのイベントのスタッフから解除しますか？`,
      type: 'danger',
    });
    if (!ok) return;
    await remove(ref(db, `events/${eventId}/staff_members/${staffUid}`));
  };

  const handleSaveMemo = async (eventId, staffUid, memo) => {
    await set(ref(db, `events/${eventId}/staff_members/${staffUid}/memo`), memo);
    setEditingMemo(null);
  };

  const eventList = Object.entries(events).map(([id, e]) => ({ id, ...e }));

  if (loading) return <div style={styles.loading}>読み込み中...</div>;

  // ─── InviteButton ───────────────────────────────────────────────────────────
  const InviteButton = ({ eventId }) => {
    const busy = inviteLoading === eventId;
    return (
      <button
        onClick={() => handleCopyInviteLink(eventId)}
        disabled={busy || isStaffLimitReached}
        style={{
          background: isStaffLimitReached ? '#f1f5f9' : '#6c63ff',
          color: isStaffLimitReached ? '#94a3b8' : '#fff',
          border: 'none',
          padding: '7px 14px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '700',
          cursor: isStaffLimitReached ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
        title={isStaffLimitReached ? `上限 ${limits.maxStaff} 人に達しています` : '招待リンクを発行してクリップボードにコピー'}
      >
        {busy ? '処理中...' : '🔗 招待リンクをコピー'}
      </button>
    );
  };

  // ─── Staff row ───────────────────────────────────────────────────────────────
  const StaffRow = ({ s, showEvent }) => {
    const isEditing = editingMemo?.eventId === s.eventId && editingMemo?.staffUid === s.uid;
    return (
      <tr style={styles.tr}>
        <td style={styles.td}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              background: '#ede9fe', color: '#6c63ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: '800', fontSize: '14px', flexShrink: 0,
            }}>
              {(s.display_name || '?').charAt(0).toUpperCase()}
            </div>
            <span style={styles.staffName}>{s.display_name || '（名前未設定）'}</span>
          </div>
        </td>
        {showEvent && (
          <td style={styles.td}>
            <span style={styles.eventTag}>{s.eventName}</span>
          </td>
        )}
        <td style={styles.td}>
          {isEditing ? (
            <div style={styles.memoEdit}>
              <input
                style={styles.memoInput}
                value={editingMemo.value}
                onChange={(e) => setEditingMemo({ ...editingMemo, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveMemo(s.eventId, s.uid, editingMemo.value);
                  if (e.key === 'Escape') setEditingMemo(null);
                }}
                autoFocus
              />
              <button style={styles.saveButton} onClick={() => handleSaveMemo(s.eventId, s.uid, editingMemo.value)}>保存</button>
              <button style={styles.cancelButton} onClick={() => setEditingMemo(null)}>✕</button>
            </div>
          ) : (
            <span
              style={styles.memoText}
              onClick={() => setEditingMemo({ eventId: s.eventId, staffUid: s.uid, value: s.memo || '' })}
              title="クリックして編集"
            >
              {s.memo || <span style={{ color: '#94a3b8' }}>メモなし（クリックで追加）</span>}
            </span>
          )}
        </td>
        <td style={styles.td}>
          <span style={styles.dateText}>
            {s.joined_at ? new Date(s.joined_at).toLocaleDateString('ja-JP') : '-'}
          </span>
        </td>
        <td style={styles.td}>
          <button
            style={styles.removeButton}
            onClick={() => handleRemoveStaff(s.eventId, s.uid, s.display_name)}
          >
            解除
          </button>
        </td>
      </tr>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const isAllView = selectedEventId === 'all';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👤 スタッフ管理</h1>
          <p style={styles.subtitle}>
            {isSuperAdmin
              ? '全イベントのスタッフを管理します'
              : `担当イベントのスタッフを管理します（${uniqueStaffCount} / ${formatMax(limits.maxStaff)} 人）`}
          </p>
          {isStaffLimitReached && (
            <p style={styles.limitWarning}>⚠️ スタッフ上限に達しています。Proプランで無制限に招待できます。</p>
          )}
        </div>

        {/* 特定イベント選択時はヘッダーにも招待ボタン */}
        {!isAllView && <InviteButton eventId={selectedEventId} />}
      </div>

      {/* Staff capabilities info */}
      <div style={{
        background: '#f5f3ff',
        border: '1px solid #ddd6fe',
        borderRadius: '12px',
        padding: '14px 18px',
        marginBottom: '20px',
        display: 'flex',
        gap: '32px',
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: '800', color: '#6c63ff', letterSpacing: '0.5px', marginBottom: '6px' }}>✅ スタッフができること</div>
          <ul style={{ margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {['キャスト・ゲストの追加・編集・削除', '優先度・指名リストの設定', '抽選の実行・結果確認', '応募フォームの作成・公開・管理'].map(t => (
              <li key={t} style={{ fontSize: '12px', color: '#4c1d95' }}>{t}</li>
            ))}
          </ul>
        </div>
        <div>
          <div style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px', marginBottom: '6px' }}>🚫 スタッフができないこと</div>
          <ul style={{ margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {['イベントの削除', '他スタッフの招待'].map(t => (
              <li key={t} style={{ fontSize: '12px', color: '#64748b' }}>{t}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Filter */}
      <div style={styles.filterRow}>
        <label style={styles.filterLabel}>イベントで絞り込み：</label>
        <select
          style={styles.filterSelect}
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          <option value="all">すべてのイベント</option>
          {eventList.map((e) => (
            <option key={e.id} value={e.id}>{e.event_name}</option>
          ))}
        </select>
      </div>

      {/* ── ALL VIEW: イベントごとにカード、列幅を統一 ── */}
      {isAllView ? (
        eventList.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
            <p>イベントがありません</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {eventList.map((event) => {
              const members = staffByEvent[event.id] || {};
              const memberEntries = Object.entries(members).map(([uid, m]) => ({
                eventId: event.id, eventName: event.event_name, uid, ...m,
              }));
              return (
                <div key={event.id} style={{
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e8edf5',
                  borderLeft: '4px solid #6c63ff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                }}>
                  {/* イベントヘッダー */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 20px',
                    background: '#f8fafc',
                    borderBottom: memberEntries.length > 0 ? '1px solid #e8edf5' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>{event.event_name}</span>
                      <span style={{
                        fontSize: '11px', fontWeight: '700',
                        background: '#ede9fe', color: '#6c63ff',
                        borderRadius: '999px', padding: '2px 8px',
                      }}>{memberEntries.length} 人</span>
                    </div>
                    <InviteButton eventId={event.id} />
                  </div>

                  {/* スタッフテーブル（列幅をcolgroup で統一） */}
                  {memberEntries.length === 0 ? (
                    <div style={{ padding: '14px 20px', fontSize: '13px', color: '#94a3b8' }}>
                      まだスタッフがいません。右の招待リンクからスタッフを招待してください。
                    </div>
                  ) : (
                    <table style={{ ...styles.table, tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '28%' }} />
                        <col style={{ width: '36%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '20%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={styles.th}>名前</th>
                          <th style={styles.th}>メモ</th>
                          <th style={styles.th}>参加日</th>
                          <th style={styles.th}>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberEntries.map((s) => (
                          <StaffRow key={`${s.eventId}-${s.uid}`} s={s} showEvent={false} />
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* ── SINGLE EVENT VIEW ── */
        (() => {
          const members = Object.entries(staffByEvent[selectedEventId] || {}).map(([uid, m]) => ({
            eventId: selectedEventId, eventName: events[selectedEventId]?.event_name || '', uid, ...m,
          }));
          return members.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
              <p>このイベントにスタッフはいません。</p>
              <p style={{ fontSize: '13px', color: '#94a3b8' }}>上の「招待リンクをコピー」ボタンからスタッフを招待してください。</p>
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>名前</th>
                    <th style={styles.th}>メモ</th>
                    <th style={styles.th}>参加日</th>
                    <th style={styles.th}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((s) => (
                    <StaffRow key={`${s.eventId}-${s.uid}`} s={s} showEvent={false} />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()
      )}
    </div>
  );
};

const styles = {
  container: { padding: '40px', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 6px 0' },
  subtitle: { fontSize: '14px', color: '#64748b', margin: '0 0 4px 0' },
  limitWarning: { fontSize: '13px', color: '#f59e0b', fontWeight: '600', margin: '4px 0 0 0' },
  filterRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  filterLabel: { fontSize: '14px', color: '#475569', fontWeight: '600' },
  filterSelect: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', background: '#fff', cursor: 'pointer', outline: 'none' },
  emptyState: { textAlign: 'center', color: '#64748b', padding: '60px 20px', fontSize: '15px' },
  eventSection: {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #e8edf5',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  eventSectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px',
    borderBottom: '1px solid #f1f5f9',
    background: '#f8fafc',
  },
  eventSectionTitle: { fontSize: '15px', fontWeight: '800', color: '#0f172a', marginRight: '8px' },
  eventSectionCount: { fontSize: '12px', color: '#94a3b8', fontWeight: '600' },
  sectionEmpty: { padding: '20px 24px', fontSize: '13px', color: '#94a3b8' },
  tableContainer: { background: '#fff', borderRadius: '12px', border: '1px solid #e8edf5', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: { padding: '12px 20px', background: '#f8fafc', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 20px', verticalAlign: 'middle' },
  staffName: { fontWeight: '600', color: '#1e293b', fontSize: '14px' },
  eventTag: { background: '#ede9fe', color: '#5b21b6', borderRadius: '12px', padding: '3px 10px', fontSize: '13px', fontWeight: '600' },
  memoText: { fontSize: '13px', color: '#475569', cursor: 'pointer' },
  memoEdit: { display: 'flex', gap: '6px', alignItems: 'center' },
  memoInput: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #6c63ff', fontSize: '13px', outline: 'none', flex: 1 },
  saveButton: { background: '#6c63ff', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  cancelButton: { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', cursor: 'pointer', color: '#64748b' },
  dateText: { fontSize: '12px', color: '#94a3b8' },
  removeButton: { background: 'transparent', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  loading: { padding: '40px', textAlign: 'center', color: '#64748b' },
};

export default StaffPage;
